import { useCallback, useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import type { IVendasCreditosResponse } from "@/hooks/vendas-creditos/listar-total-creditos-por-cliente";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { EXTRATO_CREDITO_QUERY_STALE_TIME } from "@/common/const/query-cache";
import {
  handleCobrancaCreditosPorCliente,
  handleVendasTotalCreditosPorCliente,
} from "@/domain/use-cases/vendas-creditos";
import {
  IFiltrosCobrancaCreditoCliente,
  IParametrosConsultaCobrancaCreditoCliente,
  IRespostaCobrancaCreditoCliente,
} from "@/interface/extrato-credito-cliente";
import { obterIntervaloMesAtual } from "@/utils/credito/obter-intervalo-mes-atual";

const ITENS_POR_PAGINA_PADRAO_COBRANCA = 10;

function obterFiltrosMesAtual(): IFiltrosCobrancaCreditoCliente {
  const { dataInicio, dataFim } = obterIntervaloMesAtual();
  return { dataInicio, dataFim };
}

type UseCobrancaCreditosClienteArgs = {
  clienteId: number | undefined;
};

export type IOpcaoProdutoFiltroCobranca = {
  produtoId: number;
  nome: string;
};

export function useCobrancaCreditosCliente({ clienteId }: UseCobrancaCreditosClienteArgs) {
  const { token: accessToken } = useStoreZustandUserAuth();

  const [paginaAtualCobranca, setPaginaAtualCobranca] = useState(1);
  const [itensPorPaginaCobranca, setItensPorPaginaCobranca] = useState(
    ITENS_POR_PAGINA_PADRAO_COBRANCA
  );
  const [filtrosCobranca, setFiltrosCobranca] =
    useState<IFiltrosCobrancaCreditoCliente>(obterFiltrosMesAtual);

  const parametrosConsultaCobranca: IParametrosConsultaCobrancaCreditoCliente = useMemo(
    () => ({
      paginaAtual: paginaAtualCobranca,
      itensPorPagina: itensPorPaginaCobranca,
      produtoIdFiltro: filtrosCobranca.produtoId,
      dataInicioFiltro: filtrosCobranca.dataInicio,
      dataFimFiltro: filtrosCobranca.dataFim,
    }),
    [paginaAtualCobranca, itensPorPaginaCobranca, filtrosCobranca]
  );

  const chaveCacheCobrancaCliente = useMemo(
    () => ["cobranca-creditos-cliente", clienteId, parametrosConsultaCobranca] as const,
    [clienteId, parametrosConsultaCobranca]
  );

  const {
    data: respostaCobrancaBruta,
    isLoading: carregandoCobranca,
    isFetching: buscandoCobranca,
    isError: erroCobranca,
    refetch: recarregarCobranca,
  } = useQuery<IRespostaCobrancaCreditoCliente | null>({
    queryKey: chaveCacheCobrancaCliente,
    enabled: Boolean(clienteId && accessToken),
    placeholderData: (previousData) => previousData,
    queryFn: async ({ queryKey }) => {
      const [, idClienteConsulta, parametrosDaConsulta] = queryKey as readonly [
        string,
        number | undefined,
        IParametrosConsultaCobrancaCreditoCliente,
      ];

      if (!accessToken || !idClienteConsulta) {
        return null;
      }

      const resultado = await handleCobrancaCreditosPorCliente(
        accessToken,
        idClienteConsulta,
        parametrosDaConsulta
      );

      if (resultado && typeof resultado === "object" && "totais" in resultado) {
        return resultado as IRespostaCobrancaCreditoCliente;
      }

      return null;
    },
    staleTime: EXTRATO_CREDITO_QUERY_STALE_TIME,
  });

  const chaveOpcoesProdutoCobranca = useMemo(
    () => ["cobranca-opcoes-produto-cliente", clienteId] as const,
    [clienteId]
  );

  const { data: respostaCreditosCliente, isLoading: carregandoOpcoesProdutoCobranca } =
    useQuery<IVendasCreditosResponse | null>({
      queryKey: chaveOpcoesProdutoCobranca,
      enabled: Boolean(clienteId && accessToken),
      queryFn: async () => {
        if (!accessToken || !clienteId) {
          return null;
        }
        const resultado = await handleVendasTotalCreditosPorCliente(accessToken, clienteId);
        if (resultado && typeof resultado === "object" && "data" in resultado) {
          return resultado as IVendasCreditosResponse;
        }
        return null;
      },
      staleTime: EXTRATO_CREDITO_QUERY_STALE_TIME,
    });

  const opcoesProdutoFiltroCobranca: IOpcaoProdutoFiltroCobranca[] = useMemo(() => {
    const linhas = respostaCreditosCliente?.data?.creditos ?? [];
    return linhas
      .map((linha) => {
        const id = linha.produto_id;
        if (id == null || Number.isNaN(Number(id))) {
          return null;
        }
        return { produtoId: Number(id), nome: linha.tipo };
      })
      .filter((item): item is IOpcaoProdutoFiltroCobranca => item !== null);
  }, [respostaCreditosCliente]);

  const linhasCobranca = respostaCobrancaBruta?.data ?? [];
  const paginacaoCobranca = respostaCobrancaBruta?.pagination;
  const totaisCobranca = respostaCobrancaBruta?.totais;
  const totaisConsumoPorProduto = respostaCobrancaBruta?.totais_por_produto ?? [];

  useEffect(() => {
    const ultimaPaginaDisponivel = paginacaoCobranca?.last_page;
    if (ultimaPaginaDisponivel != null && paginaAtualCobranca > ultimaPaginaDisponivel) {
      setPaginaAtualCobranca(ultimaPaginaDisponivel);
    }
  }, [paginacaoCobranca?.last_page, paginaAtualCobranca]);

  const definirPaginaAtualCobranca = useCallback((novaPagina: number) => {
    setPaginaAtualCobranca(Math.max(1, novaPagina));
  }, []);

  const definirFiltrosCobranca = useCallback((patch: Partial<IFiltrosCobrancaCreditoCliente>) => {
    setFiltrosCobranca((anterior) => ({ ...anterior, ...patch }));
    setPaginaAtualCobranca(1);
  }, []);

  const limparFiltrosCobranca = useCallback(() => {
    setFiltrosCobranca({});
    setPaginaAtualCobranca(1);
  }, []);

  const aplicarFiltroMesAtual = useCallback(() => {
    const { dataInicio, dataFim } = obterIntervaloMesAtual();
    definirFiltrosCobranca({ dataInicio, dataFim });
  }, [definirFiltrosCobranca]);

  return {
    linhasCobranca,
    paginacaoCobranca,
    totaisCobranca,
    totaisConsumoPorProduto,
    carregandoCobranca,
    buscandoCobranca,
    erroCobranca,
    paginaAtualCobranca,
    itensPorPaginaCobranca,
    setItensPorPaginaCobranca,
    definirPaginaAtualCobranca,
    recarregarCobranca,
    filtrosCobranca,
    definirFiltrosCobranca,
    limparFiltrosCobranca,
    aplicarFiltroMesAtual,
    opcoesProdutoFiltroCobranca,
    carregandoOpcoesProdutoCobranca,
  };
}
